import React, { useEffect, useState } from 'react';
import { boolToForm } from 'sellDirect/helpers/closingDash/formatters';
import { Box } from '@opendoor/bricks/core';
import { showToast } from '@opendoor/bricks/complex/Toast';
import {
  SPRINKLER_TIMER_TYPE_MAP,
  OCCUPANCY_STATUS_AT_ACQUISITION,
  isStaticDisclosuresFormValid,
  staticDisclosuresFormToClosingTasks,
  isStaticDisclosureFormComplete,
} from 'sellDirect/helpers/closingDash/disclosures';

import {
  IResetableFormProps,
  ResetableForm,
} from 'sellDirect/components/closingDash/ResetableForm';
import { UnderContractOffer } from 'sellDirect/OfferContext';
import { useUpdateClosingTasks } from 'sellDirect/graphql/useUpdateClosingTasks';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { DisclosuresFormContent } from 'sellDirect/components/closingDash/disclosures/DisclosuresFormContent';
import { DisclosuresSummary } from 'sellDirect/components/closingDash/disclosures/DisclosuresSummary';

export interface DisclosuresFormState {
  hasWaterDamage?: string;
  hasWaterDamageDetails?: string;
  hadPlumbingRepairs?: string;
  hadPlumbingRepairsDetails?: string;
  hasExistingWaterIssue?: string;
  hasExistingWaterIssueDetails?: string;
  hasSewerSepticIssue?: string;
  hasSewerSepticIssueDetails?: string;
  hasEncroachedOrSharedProperty?: string;
  hasEncroachedOrSharedPropertyDetails?: string;
  hasDispute?: string;
  hasDisputeDetails?: string;
  coolingType?: string;
  heatingType?: string;
  occupancyStatusAtAcquisition?: string | null | undefined;
  otherOccupancyStatusAtAcquisition?: string | null | undefined;
  filedClaim?: string;
  damageClaimDetails?: string;
  hasPolybutenePiping?: string;
  hasSeptic?: string;
  hasSprinklerOrIrrigation?: string;
  hasSumpPump?: string;
  hasWellWaterSupply?: string;
  hvacAge?: string;
  lastFoundationEvaluation?: string;
  receivedProceeds?: string;
  roofAge?: string;
  roofStormDamage?: string;
  sprinklerTimerType?: string;
  workingSprinkler?: string;
}

export interface DisclosuresFormProps {
  offer: UnderContractOffer;
  closingTasks: ClosingTasks;
  setInEditMode: (inEditMode: boolean) => void;
}

/**
 * HVAC and roof form controller.
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const DisclosuresForm: React.FC<DisclosuresFormProps> = ({
  offer,
  closingTasks,
  setInEditMode,
}) => {
  //Plumbing and Irrigation with HVAC
  const hvacRoofInfo = closingTasks.hvacRoofInfo?.taskable;
  //Disclosures Utilities
  const serviceAccounts = closingTasks?.serviceAccounts?.taskable;
  //Other home information
  const otherDisclosures = closingTasks.otherHomeInfo?.taskable;

  const shouldAskPolybutenePiping = offer.closingExperience?.shouldAskPolybutenePiping ?? undefined;
  const shouldAskStormRoofDamage =
    offer.closingExperience?.shouldAskStormAndFoundation ?? undefined;
  const isOtherOccupancyStatusAtAcquisition =
    otherDisclosures?.occupancyStatusAtAcquisition &&
    ![
      OCCUPANCY_STATUS_AT_ACQUISITION.tenantOccupied,
      OCCUPANCY_STATUS_AT_ACQUISITION.ownerOccupied,
      OCCUPANCY_STATUS_AT_ACQUISITION.vacantHome,
    ].some((status) => status === otherDisclosures?.occupancyStatusAtAcquisition);
  //TODO put this in a helpers function called isDisclosuresFormComplete and import it to replace this
  const isStaticFormComplete = isStaticDisclosureFormComplete(closingTasks, offer);

  const [formState, setFormState] = useState<DisclosuresFormState>({
    //HVAC Roof Info
    filedClaim: boolToForm(hvacRoofInfo?.filedClaim ? true : undefined, isStaticFormComplete),
    damageClaimDetails: hvacRoofInfo?.damageClaimDetails ?? undefined,
    hasPolybutenePiping: boolToForm(
      hvacRoofInfo?.hasPolybutenePiping ? true : undefined,
      isStaticFormComplete,
    ),
    hasSeptic: boolToForm(hvacRoofInfo?.hasSeptic ? true : undefined, isStaticFormComplete),
    hasSprinklerOrIrrigation: boolToForm(
      hvacRoofInfo?.hasSprinklerOrIrrigation ? true : undefined,
      isStaticFormComplete,
    ),
    hasSumpPump: boolToForm(hvacRoofInfo?.hasSumpPump ? true : undefined, isStaticFormComplete),
    hasWellWaterSupply: boolToForm(
      hvacRoofInfo?.hasWellWaterSupply ? true : undefined,
      isStaticFormComplete,
    ),
    hvacAge: hvacRoofInfo?.hvacAge ?? undefined,
    lastFoundationEvaluation: hvacRoofInfo?.lastFoundationEvaluation ?? undefined,
    receivedProceeds: boolToForm(
      hvacRoofInfo?.receivedProceeds ? true : undefined,
      isStaticFormComplete,
    ),
    roofAge: hvacRoofInfo?.roofAge ?? undefined,
    roofStormDamage: boolToForm(
      hvacRoofInfo?.roomStormDamage ? true : undefined,
      isStaticFormComplete,
    ),
    sprinklerTimerType: hvacRoofInfo?.sprinklerTimerType
      ? SPRINKLER_TIMER_TYPE_MAP[hvacRoofInfo?.sprinklerTimerType]
      : undefined,
    workingSprinkler: isStaticFormComplete ? boolToForm(hvacRoofInfo?.workingSprinkler) : undefined,
    //Disclosure Utilities
    heatingType: serviceAccounts?.heatingType ?? undefined,
    coolingType: serviceAccounts?.coolingType ?? undefined,
    hasExistingWaterIssue: boolToForm(
      serviceAccounts?.hasExistingWaterIssue ? true : undefined,
      isStaticFormComplete,
    ),
    hasExistingWaterIssueDetails: serviceAccounts?.hasExistingWaterIssueDetails ?? undefined,
    hasSewerSepticIssue: boolToForm(
      serviceAccounts?.hasSewerSepticIssue ? true : undefined,
      isStaticFormComplete,
    ),
    hasSewerSepticIssueDetails: serviceAccounts?.hasSewerSepticIssueDetails ?? undefined,
    //Other Home Info
    hasDispute: boolToForm(otherDisclosures?.hasDispute ? true : undefined, isStaticFormComplete),
    hasDisputeDetails: otherDisclosures?.hasDisputeDetails ?? undefined,
    hasEncroachedOrSharedProperty: boolToForm(
      otherDisclosures?.hasEncroachedOrSharedProperty ? true : undefined,
      isStaticFormComplete,
    ),
    hasEncroachedOrSharedPropertyDetails:
      otherDisclosures?.hasEncrochedOrSharedPropertyDetails ?? undefined,
    hadPlumbingRepairs: boolToForm(
      otherDisclosures?.hadPlumbingRepairs ? true : undefined,
      isStaticFormComplete,
    ),
    hadPlumbingRepairsDetails: otherDisclosures?.hadPlumbingRepairsDetails ?? undefined,
    hasWaterDamage: boolToForm(
      otherDisclosures?.hasWaterDamage ? true : undefined,
      isStaticFormComplete,
    ),
    hasWaterDamageDetails: otherDisclosures?.hasWaterDamageDetails ?? undefined,
    occupancyStatusAtAcquisition: isOtherOccupancyStatusAtAcquisition
      ? OCCUPANCY_STATUS_AT_ACQUISITION.other
      : otherDisclosures?.occupancyStatusAtAcquisition,
    otherOccupancyStatusAtAcquisition: isOtherOccupancyStatusAtAcquisition
      ? otherDisclosures?.occupancyStatusAtAcquisition
      : null,
  });

  const [isEditing, setIsEditing] = useState(
    !isStaticDisclosuresFormValid(formState, shouldAskPolybutenePiping, shouldAskStormRoofDamage),
  );
  const { updateClosingTasks, loading } = useUpdateClosingTasks('update-disclosures', {
    onCompleted: () => {
      setIsEditing(false);
    },
    onError: () => {
      setIsEditing(true);
      showToast(
        'error',
        'Oops, something went wrong. Please try again in a few hours or contact your experience partner.',
        'cake-update-disclosures-error-toast',
        { toastOptions: { position: 'bottom-left' } },
      );
    },
    errorPolicy: 'none',
  });

  const onSubmit: IResetableFormProps['onSubmit'] = (resetableForm) => {
    setFormState(resetableForm?.formState);
    const tasks = staticDisclosuresFormToClosingTasks(resetableForm.formState);
    updateClosingTasks(tasks);
  };

  useEffect(() => {
    setInEditMode(isEditing);
  }, [setInEditMode, isEditing]);

  return (
    <Box>
      {isEditing ? (
        <ResetableForm
          initialFormState={formState}
          onSubmit={onSubmit}
          render={(resetableForm) => (
            <DisclosuresFormContent
              offer={offer}
              closingTasks={closingTasks}
              loading={loading}
              onCancel={() => setIsEditing(false)}
              resetableForm={resetableForm}
            />
          )}
        />
      ) : (
        <DisclosuresSummary
          closingTasks={closingTasks}
          offer={offer}
          setIsEditingForm={setIsEditing}
        />
      )}
    </Box>
  );
};
